import React from "react";
import { ToastContainer } from "react-toastify";
import { toast as rtToast, ToastOptions } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  ToastSuccessIcon,
  ToastInfoIcon,
  ToastWarningIcon,
  ToastErrorIcon,
} from "./toast-icons";
import "./toast-overrides.css";

const icons = {
  success: <ToastSuccessIcon />,
  info: <ToastInfoIcon />,
  warning: <ToastWarningIcon />,
  error: <ToastErrorIcon />,
} as const;

type ToastType = keyof typeof icons;

const DEFAULT_TITLES: Record<ToastType, string> = {
  success: "Success",
  info: "Info",
  warning: "Warning",
  error: "Error",
};

function getIcon(type: ToastType) {
  return icons[type];
}

function renderContent(
  message: React.ReactNode,
  type: ToastType,
  title?: string
): React.ReactNode {
  const displayTitle = title ?? DEFAULT_TITLES[type];
  return (
    <span className="toast-content">
      <span className="toast-title">{displayTitle}</span>
      <span className="toast-message">{message}</span>
    </span>
  );
}

export function AppToastContainer() {
  return (
    <ToastContainer
      theme="light"
      className="app-toast-container"
      position="top-right"
      autoClose={5000}
      hideProgressBar
      closeOnClick
      pauseOnHover
      draggable
    />
  );
}

export interface AppToastOptions extends ToastOptions {
  type?: ToastType;
  
  title?: string;
}

export const appToast = {
  success: (message: React.ReactNode, options?: AppToastOptions) =>
    rtToast.success(renderContent(message, "success", options?.title), {
      ...options,
      icon: getIcon("success"),
    }),

  info: (message: React.ReactNode, options?: AppToastOptions) =>
    rtToast.info(renderContent(message, "info", options?.title), {
      ...options,
      icon: getIcon("info"),
    }),

  warning: (message: React.ReactNode, options?: AppToastOptions) =>
    rtToast.warning(renderContent(message, "warning", options?.title), {
      ...options,
      icon: getIcon("warning"),
    }),

  error: (message: React.ReactNode, options?: AppToastOptions) =>
    rtToast.error(renderContent(message, "error", options?.title), {
      ...options,
      icon: getIcon("error"),
    }),

  
  show: (message: React.ReactNode, options?: AppToastOptions) => {
    const type = options?.type ?? "success";
    return rtToast(renderContent(message, type, options?.title), {
      ...options,
      type,
      icon: getIcon(type),
    });
  },
};
